import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Printer, LogOut, MapPin, KeyRound, MonitorX } from "lucide-react";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";

function DepotSwitcher() {
  const { isAdmin, user } = useAuth();
  const { depots, selected, setSelected, depotName } = useDepot();
  if (!isAdmin) {
    return (
      <div data-testid="employee-depot-badge" className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-300">
        <MapPin className="h-4 w-4" />
        <span className="font-medium truncate max-w-[140px] sm:max-w-none">{user.role === "truck_manager" ? "Truck Fleet · Dar es Salaam" : depotName(user.depot_id)}</span>
      </div>
    );
  }
  return (
    <Select value={selected} onValueChange={setSelected}>
      <SelectTrigger data-testid="depot-selector-dropdown" className="w-[170px] sm:w-[220px] bg-slate-900 border-slate-700">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="depot-option-all">All Depots</SelectItem>
        {depots.map((d) => (
          <SelectItem key={d.id} value={d.id} data-testid={`depot-option-${d.code}`}>{d.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function UserMenu() {
  const { user, logout, logoutAll } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const initials = user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu-trigger" className="h-9 w-9 rounded-full bg-slate-800 border border-slate-700 font-mono text-xs font-bold text-slate-200 hover:border-amber-500 transition-colors">
          {initials}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm font-semibold" data-testid="user-menu-name">{user.name}</div>
          <div className="text-xs text-slate-400 truncate">{user.email}</div>
          <div className="label-mono mt-1 !text-amber-400" data-testid="user-menu-role">{user.role === "admin" ? "Owner / Admin" : user.role === "truck_manager" ? "Truck Manager" : "Employee"}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setPwOpen(true)} data-testid="change-password-menu-item" className="cursor-pointer">
          <KeyRound className="h-4 w-4 mr-2" /> Change password
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-rose-400 cursor-pointer">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logoutAll} data-testid="logout-all-button" className="text-rose-400 cursor-pointer">
          <MonitorX className="h-4 w-4 mr-2" /> Sign out all devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </>
  );
}

export function HeaderBar({ onMenu }) {
  const { user } = useAuth();
  return (
    <header className="no-print sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-800 bg-slate-950/80 px-4 backdrop-blur-md md:px-6 lg:px-8">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-button">
        <Menu className="h-5 w-5" />
      </Button>
      <DepotSwitcher />
      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {user.role !== "truck_manager" && (
        <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="header-daily-report-button">
          <Link to="/report">
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Daily Report</span>
          </Link>
        </Button>
        )}
        <UserMenu />
      </div>
    </header>
  );
}
